import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  createInsurer,
  insurerDetailPath,
  listInsurers,
} from "@/core/insurance/insurer-store";
import type { InsurerInput } from "@/core/insurance/types";
import { listPatientsByInsurer } from "@/core/patients/patient-store";
import { routes } from "@/router/all_routes";

export function InsuranceInsurersPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<InsurerInput>({
    code: "",
    name: "",
    licenseNumber: "",
    phone: "",
    email: "",
    contactPerson: "",
  });

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:insurers", refresh);
    return () => window.removeEventListener("hospitol:insurers", refresh);
  }, []);

  const insurers = useMemo(() => {
    void refreshKey;
    return listInsurers();
  }, [refreshKey]);

  const pagination = useClientPagination(insurers, [], 25);
  const { pagedItems } = pagination;

  const handleCreate = (e: FormEvent) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) return;
    createInsurer(form);
    setForm({ code: "", name: "", licenseNumber: "", phone: "", email: "", contactPerson: "" });
    setShowForm(false);
    setRefreshKey((k) => k + 1);
  };

  return (
    <div>
      <PageHeader
        title="Compagnies d'assurance"
        description="Clients CRM — mutuelles et assureurs partenaires (tiers payant)."
        actions={
          <div className="d-flex gap-2 flex-wrap ph-page-header-actions">
            <Link to={routes.insuranceHome} className="btn btn-outline-secondary btn-sm">
              Assurance médicale
            </Link>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowForm((v) => !v)}
            >
              {showForm ? "Annuler" : "Nouvel assureur"}
            </button>
          </div>
        }
      />

      {showForm && (
        <form className="ph-invoice-sheet mb-4 row g-2 ph-lab-register-form" onSubmit={handleCreate}>
          <div className="col-12 col-sm-6 col-md-2">
            <input
              className="form-control form-control-sm"
              placeholder="Code *"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            />
          </div>
          <div className="col-12 col-md-3">
            <input
              className="form-control form-control-sm"
              placeholder="Raison sociale *"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-2">
            <input
              className="form-control form-control-sm"
              placeholder="N° agrément"
              value={form.licenseNumber}
              onChange={(e) => setForm((f) => ({ ...f, licenseNumber: e.target.value }))}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-2">
            <input
              className="form-control form-control-sm"
              placeholder="Contact"
              value={form.contactPerson}
              onChange={(e) => setForm((f) => ({ ...f, contactPerson: e.target.value }))}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-2">
            <button type="submit" className="btn btn-success btn-sm w-100">
              Enregistrer
            </button>
          </div>
        </form>
      )}

      <div className="ph-invoice-sheet">
        <div className="table-responsive ph-diag-table">
          <table className="table table-sm ph-dash-table align-middle mb-0">
            <thead>
              <tr>
                <th>Code</th>
                <th>Assureur</th>
                <th>Agrément</th>
                <th>Contact</th>
                <th>Adhérents</th>
                <th>Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pagedItems.map((ins) => (
                <tr key={ins.id} className="ph-diag-row">
                  <td data-label="Code" className="ph-inv-badge">
                    {ins.code}
                  </td>
                  <td data-label="Assureur" className="fw-medium">
                    {ins.name}
                  </td>
                  <td data-label="Agrément" className="small">
                    {ins.licenseNumber || "—"}
                  </td>
                  <td data-label="Contact" className="small">
                    {ins.contactPerson || "—"}
                  </td>
                  <td data-label="Adhérents">{listPatientsByInsurer(ins.id).length}</td>
                  <td data-label="Statut">
                    <span className={`badge ${ins.active ? "bg-success" : "bg-secondary"}`}>
                      {ins.active ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td data-label="Action" className="text-end">
                    <Link to={insurerDetailPath(ins.id)} className="btn btn-link btn-sm p-0">
                      Ouvrir
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            totalItems={pagination.totalItems}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
          />
        </div>
      </div>
    </div>
  );
}
